import { ExtractedEntities } from "../types";
import { COLLOQUIAL_FAILURE_WORDS } from "../../utils/incidentParser";

export interface Classification {
  path: "A" | "B";
  confidence: number;
  aScore: number;
  bScore: number;
}

// Słownik awarii/usterek jest z natury nieograniczony (każda możliwa maszyna,
// każdy możliwy sposób jej zepsucia) — próba wyliczenia WSZYSTKICH słów-wyzwalaczy
// ścieżki B jest z góry skazana na dziury. Znacznie pewniejszy sygnał to odwrotność:
// ścieżka A (rozmowa szefa z pracownikiem) ma wąski, przewidywalny zestaw wyzwalaczy —
// imię na początku wypowiedzi oraz zwroty o ZACHOWANIU/POSTAWIE człowieka, a nie o stanie
// maszyny. Dlatego te słowa mają wyższą wagę niż ogólne bWords.
// Rdzenie zamiast sztywnych fraz — mowa potoczna często odwraca kolejność słów
// ("się spóźnił" zamiast "spóźnił się"), więc dopasowanie do dokładnej frazy
// gubiło realne wypowiedzi. Rdzeń łapie obie kolejności i wszystkie odmiany.
const A_CONDUCT_WORDS = [
  "spóźni", // spóźnił/spóźniła/spóźnia się/spóźnienie
  "nie przyszedł do pracy", "nie przyszła do pracy", "nieobecnoś", "absencj",
  "nie stawił się", "nie stawiła się",
  "kłóci", "awantur", "konflikt z",
  "nie wykonał polecenia", "nie wykonała polecenia",
  "zlekceważ", "lekceważy", "ignoruje polecenia",
  "notorycznie", "permanentnie", "wielokrotnie już",
  "pod wpływem alkoholu", "pijany", "pijana", "nietrzeźw",
  "agresywnie się zach", "agresywne zachowanie",
  "brak zaangażowania", "niewywiązuje się", "nie wywiązuje się z obowiązków",
  "opuścił stanowisko", "opuściła stanowisko",
  "samowolnie oddal",
  "niesubordynacj",
  "rozmowa dyscyplinując", "upomnienie dla", "nagana dla", "ostrzeżenie dla pracownika",
  "niewłaściwe zachowanie", "nieodpowiednie zachowanie",
  "obraził", "obraziła", "wyzywał", "wyzywała",
];

// Czy imię pracownika pojawia się na samym początku wypowiedzi — klasyczny wzorzec
// rozmowy o pracowniku ("Kowalski znowu się spóźnił...", "Ania nie wykonała...").
function employeeNameNearStart(text: string, employeeCanonical?: string): boolean {
  if (!employeeCanonical) return false;
  const firstWords = text.trim().toLowerCase().split(/\s+/).slice(0, 3).join(" ");
  return employeeCanonical
    .toLowerCase()
    .split(/\s+/)
    .some(part => part.length > 2 && firstWords.includes(part));
}

export function classify(entities: ExtractedEntities, transcript: string): Classification {
  const text = transcript.toLowerCase();

  let aScore = 0;
  let bScore = 0;

  if (entities.employee && entities.arrangement) aScore += 3;
  if (entities.employee && entities.date) aScore += 1;
  if (entities.arrangement) aScore += 2;
  if (employeeNameNearStart(transcript, entities.employee)) aScore += 3;

  if (entities.equipment && entities.failure) bScore += 4;
  if (entities.equipment && entities.location) bScore += 2;
  if (entities.failure) bScore += 2;
  if (entities.location && entities.failure) bScore += 2;

  // Uwaga: "awari" (rdzeń) zamiast "awaria" — żeby łapać odmiany przez przypadki
  // ("awarię", "awarii", "awarią"), bardzo częste w naturalnie mówionych zgłoszeniach.
  // Dodatkowo potoczne/emocjonalne określenia awarii (patrz incidentParser.ts) —
  // zdenerwowany brygadzista rzadko mówi "awaria" wprost.
  const bWords = ["awari", "usterka", "nie działa", "cieknie", "przegrzewa", "pęka", "zatrzymał", "zapchał", ...COLLOQUIAL_FAILURE_WORDS];
  const aWords = ["rozmowa", "ustalić", "ustalenia", "ma zrobić", "ma dostarczyć", "ma przygotować"];

  for (const w of bWords) {
    if (text.includes(w)) bScore += 1.5;
  }
  for (const w of aWords) {
    if (text.includes(w)) aScore += 1.5;
  }
  for (const w of A_CONDUCT_WORDS) {
    if (text.includes(w)) aScore += 2.5;
  }

  const threshold = 0.5;

  if (bScore > aScore && bScore >= threshold) {
    return { path: "B", confidence: Math.min(bScore / 6, 1), aScore, bScore };
  }
  if (aScore > bScore && aScore >= threshold) {
    return { path: "A", confidence: Math.min(aScore / 6, 1), aScore, bScore };
  }

  // Remis / brak sygnałów: dawniej domyślnie "A" (rozmowa) — ale skoro słownik B
  // jest ustrukturyzowany wg zakładowych słowników, a A ma teraz wyraźne, świadome
  // wyzwalacze (imię na starcie / zachowanie), bezpieczniej przy braku JAKIEGOKOLWIEK
  // sygnału potraktować to jako zgłoszenie (B) — trafi do formularza incydentu, który
  // i tak można ręcznie skorygować, zamiast fałszywie zakładać rozmowę o pracowniku.
  if (aScore === 0 && bScore === 0) {
    return { path: "B", confidence: 0.2, aScore, bScore };
  }

  return { path: "A", confidence: 0.3, aScore, bScore };
}
